import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { RankedBars } from '@/shared/ui/charts'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { coverage } from './api'
import {
  COVERAGE_ORDER, CoverageChip, coverageLabel, meterTone, pct, pctValue, ratio,
} from './vocab'

/**
 * Coverage.
 *
 * ## Two percentages, on the same row, always
 *
 * `312 / 480 stations (65%)` and `voter-weighted 81%` say different things.
 * Forty unadopted stations of eighty voters are a smaller problem than four of
 * seven hundred, and a station-count percentage hides exactly that. Every
 * response carries both and this screen prints both — a panel showing one of
 * them is the defect, not a simplification.
 *
 * ## Two columns that look like the same number and are not
 *
 * **Mobilizers** counts people *assigned* to a unit; **Adopted** counts stations
 * somebody *owns*. "Is anyone working here" and "does this station have an
 * owner" are different questions, and a unit can easily have three of one and
 * none of the other. They sit side by side precisely so the gap between them is
 * visible; collapsing them into one number is what the whole design was avoiding.
 *
 * ## Colour
 *
 * Coverage status is a *status* scale — reserved, and always with its label
 * beside it. The bars use length, not hue, because this app's categorical chart
 * tokens fail a colour-vision check on adjacent pairs and a ranked bar's length
 * already carries the ranking.
 */

const LEVELS = [
  { value: 'COUNTY', label: 'Counties' },
  { value: 'CONSTITUENCY', label: 'Constituencies' },
  { value: 'WARD', label: 'Wards' },
  { value: 'POLLING_STATION', label: 'Polling stations' },
]

export function MobilizerCoveragePage() {
  const canRead = useCan('mobilizer:read')
  const [params, setParams] = useSearchParams()

  const level = params.get('level') ?? 'WARD'
  const q = params.get('q') ?? ''
  const [statuses, setStatuses] = useState<string[]>([])
  const [root, setRoot] = useState<{ value: string; label: string }[]>([])

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  const rootId = root[0]?.value
  const data = useLoad(
    () => (canRead
      ? coverage({ level, q: q || undefined, unitId: rootId, status: statuses.length ? statuses : undefined })
      : Promise.resolve(null)),
    [canRead, level, q, rootId, statuses.join(',')])

  if (!canRead) return <Forbidden what="coverage" />

  const d = data.data
  const t = d?.totals
  const rows = d?.rows ?? []
  const gaps = rows.filter((r) => r.status === 'GAP')
  const activeCount = [q, rootId].filter(Boolean).length + (statuses.length ? 1 : 0)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Coverage"
        description={d?.scopeName
          ? `Within ${d.scopeName}. Both percentages are shown everywhere, because a station count and a voter count answer different questions.`
          : 'Both percentages are shown everywhere, because a station count and a voter count answer different questions.'}
        right={(
          <button type="button" className="erp-btn" aria-label="Refresh"
                  onClick={() => void data.reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        )}
      />

      <KpiStrip>
        <MetricCard
          label="Stations adopted"
          value={`${fmtNumber(t?.stationsAdopted ?? 0)} / ${fmtNumber(t?.stations ?? 0)}`}
          sub={<span className="erp-metric">{pct(t?.stationsAdopted ?? 0, t?.stations ?? 0)} of stations</span>}
          accent
        />
        <MetricCard
          label="Voter-weighted coverage"
          value={pct(t?.votersCovered ?? 0, t?.voters ?? 0)}
          sub={`${fmtNumber(t?.votersCovered ?? 0)} of ${fmtNumber(t?.voters ?? 0)} voters`}
        />
        <MetricCard label="Mobilizers" value={fmtNumber(t?.mobilizers ?? 0)}
                    sub="active, assigned inside this scope" />
        <MetricCard
          label="Voters per mobilizer"
          value={ratio(t?.votersPerMobilizer)}
          sub={t?.votersPerMobilizer === null || t?.votersPerMobilizer === undefined
            ? 'undefined — nobody is assigned here'
            : 'across the whole scope'}
        />
        <MetricCard label="Gaps" value={fmtNumber(t?.byStatus?.GAP ?? 0)}
                    sub={`${LEVELS.find((l) => l.value === level)?.label.toLowerCase() ?? 'units'} with nobody`}
                    delta={(t?.byStatus?.GAP ?? 0) > 0 ? 'needs staffing' : undefined}
                    deltaType="down" />
      </KpiStrip>

      <FilterBar activeCount={activeCount}>
        <select className="erp-input" value={level} aria-label="Level"
                onChange={(e) => patch({ level: e.target.value })}>
          {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <SearchInput value={q} onChange={(v) => patch({ q: v })} placeholder="Search by name" />
        <MultiSelect
          label="Status"
          options={COVERAGE_ORDER.map((c) => ({ value: c, label: coverageLabel(c) }))}
          selected={statuses}
          onChange={setStatuses}
        />
        <AsyncMultiSelect
          label="Within"
          selected={root}
          onChange={(next) => setRoot(next.slice(-1))}
          placeholder="Narrow to a county, constituency or ward"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={data.error} />

      {gaps.length > 0 && (
        <Panel
          title={`Biggest gaps (${gaps.length})`}
          description="Ranked by voters, not by count — an uncovered unit of seven hundred voters is not the same problem as one of eighty."
        >
          {/* Sorted HERE, not assumed. `RankedBars` does not sort, and the
              response documents no row order — relying on the server's happening
              to be descending is how "biggest gaps" quietly becomes "first ten
              gaps". The copy makes a promise; this is what keeps it. */}
          <RankedBars
            points={[...gaps]
              .sort((a, b) => b.voters - a.voters)
              .slice(0, 10)
              .map((g) => ({
                // Two wards can share a name. The path disambiguates them in the
                // key and in the label's tooltip.
                label: g.unitName ?? g.unitId,
                value: g.voters,
              }))}
            emptyMessage="No gaps at this level."
          />
        </Panel>
      )}

      {(t?.byStatus?.OVERSTAFFED ?? 0) > 0 && (
        <Note tone="info">
          {t?.byStatus?.OVERSTAFFED} {level === 'POLLING_STATION' ? 'stations have' : 'units have'} more
          mobilizers than the configured ratio calls for. That is not a fault — it is where people
          can be redeployed from, and the thresholds themselves are settings a coordinator can change.
        </Note>
      )}

      <Panel bodyClassName="p-0">
        <ErpTable head={<>
          <Th>{LEVELS.find((l) => l.value === level)?.label.replace(/s$/, '') ?? 'Unit'}</Th>
          <Th className="text-right">Stations</Th>
          <Th className="text-right">Adopted</Th>
          <Th>Station coverage</Th>
          <Th className="text-right">Voters</Th>
          <Th>Voter-weighted</Th>
          <Th className="text-right">Mobilizers</Th>
          <Th className="text-right">Voters each</Th>
          <Th>Status</Th>
          {level === 'POLLING_STATION' && <Th>Owner</Th>}
        </>}>
          {data.loading && !d ? (
            <LoadingRows colSpan={level === 'POLLING_STATION' ? 10 : 9} label="Counting…" />
          ) : rows.length === 0 ? (
            <EmptyRow colSpan={level === 'POLLING_STATION' ? 10 : 9}
                      message="Nothing at this level matches these filters." />
          ) : rows.map((r) => {
            // null, not 0, when the denominator is zero. A full-width red "0%"
            // reads as total failure where the truth is "there is nothing here
            // to measure" — the same null-vs-zero rule the ratio follows.
            const stationPct = r.stations ? pctValue(r.stationsAdopted, r.stations) : null
            const voterPct = r.voters ? pctValue(r.votersCovered, r.voters) : null
            return (
              <tr key={r.unitId} className="hover:bg-muted/40" data-unit={r.unitName ?? r.unitId}>
                <Td>
                  <span className="font-medium">{r.unitName ?? r.unitId}</span>
                </Td>
                <Td className="text-right erp-metric">{fmtNumber(r.stations)}</Td>
                <Td className="text-right erp-metric">{fmtNumber(r.stationsAdopted)}</Td>
                <Td><Meter value={stationPct} /></Td>
                <Td className="text-right erp-metric">{fmtNumber(r.voters)}</Td>
                <Td><Meter value={voterPct} /></Td>
                <Td className="text-right erp-metric">{fmtNumber(r.mobilizers)}</Td>
                <Td className="text-right erp-metric">{ratio(r.votersPerMobilizer)}</Td>
                <Td><CoverageChip code={r.status} /></Td>
                {level === 'POLLING_STATION' && (
                  <Td>
                    {r.adopterName ?? <span className="text-muted-foreground">Nobody</span>}
                  </Td>
                )}
              </tr>
            )
          })}
        </ErpTable>
      </Panel>
    </div>
  )
}

/** Length carries the value; the number beside it carries the reading. */
function Meter({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-muted-foreground text-erp-xs" title="Nothing here to measure">—</span>
  }
  return (
    <span className="flex items-center gap-2 min-w-24">
      <span className="h-1.5 w-16 bg-muted rounded-sm overflow-hidden shrink-0">
        <span className={cn('block h-full', meterTone(value))} style={{ width: `${value}%` }} />
      </span>
      <span className="erp-metric text-erp-xs">{value}%</span>
    </span>
  )
}
